/**
 * Aba "Outros" — dados de referência (GUPM-192). TS puro (constantes), fiel ao
 * V8 (`DOCS_REFERENCIA`, `MATRIZ_COMUNIC`). Dado de referência estático (normas,
 * códigos de documento, matriz de comunicação) — sem tabela/migration, como o
 * catálogo TARP. SEM PII: só normas, códigos e PAPÉIS/empresas.
 */

export * from './suporte-tecnico';
export * from './matriz-raci';

export type DocTipo = 'norma' | 'inspecao' | 'controle' | 'paebm' | 'manual' | 'estudo' | 'trabalho';

export interface DocReferencia {
  doc: string;
  cod: string;
  rev: string;
  tipo: DocTipo;
}

/** Documentos vigentes que fundamentam a operação (V8:1806-1824). */
export const DOCS_REFERENCIA: readonly DocReferencia[] = [
  { doc: 'Resolução ANM — segurança de barragens de mineração', cod: 'Resolução ANM nº 95/2022', rev: '2022', tipo: 'norma' },
  { doc: 'Alteração da Resolução ANM nº 95/2022', cod: 'Resolução ANM nº 130/2023', rev: '2023', tipo: 'norma' },
  { doc: 'Resolução ANM — atualização do marco de segurança de barragens', cod: 'Resolução ANM nº 220/2025', rev: '2025', tipo: 'norma' },
  { doc: 'Relatório de Inspeção de Segurança Regular — Barragem B', cod: 'RISR 1S/2026', rev: '1º sem./2026', tipo: 'inspecao' },
  { doc: 'Cartas de Controle da Barragem B (níveis de controle da instrumentação)', cod: 'WA02821250-1-GT-RTE-0001', rev: 'Rev. 2 / 2025', tipo: 'controle' },
  { doc: 'Cartas de Controle de Deslocamento — Barragem B (Walm)', cod: 'W028212501-GT-RTE-0001', rev: 'Rev. 0 / 2025', tipo: 'controle' },
  { doc: 'Plano de Ação de Emergência (PAEBM) — Barragem B, Seção I', cod: 'WA02821254-1-HL-RTE-0001', rev: 'Rev. 2 / Abr 2026', tipo: 'paebm' },
  { doc: 'PAEBM — Barragem B, Seção II (Caderno de Resposta / Defesa Civil)', cod: 'WA02821254-1-HL-RTE-0002', rev: 'Rev. 1 / Abr 2026', tipo: 'paebm' },
  { doc: 'PAEBM — Barragem A, Seção II (Caderno de Resposta / Defesa Civil)', cod: 'WA06621000-1-RH-RTE-0148', rev: 'Rev. 4 / Mai 2026', tipo: 'paebm' },
  { doc: 'PAEBM — Barragem C, Seção II (Caderno de Resposta / Defesa Civil)', cod: 'WA02821254-1-HL-RTE-0019', rev: 'Rev. 1 / Abr 2026', tipo: 'paebm' },
  { doc: 'Carta de Controle de Percolação — Barragem A (INA-10A, INA-11A, RL-01)', cod: 'MOS-24004099-RTL-001', rev: 'Rev. 0 / Out 2025', tipo: 'controle' },
  { doc: 'Carta de Controle de Deslocamento — Barragem A (MS-01 a MS-05, TxD + estatístico)', cod: 'MOS-24004143-RTL-001', rev: 'Rev. 0 / Out 2025', tipo: 'controle' },
  { doc: 'Carta de Controle de Percolação — Barragem C (INA, PZ, RL-01, MV-01)', cod: 'MOS-24004143-RTL-002', rev: 'Rev. 1 / Mar 2026', tipo: 'controle' },
  { doc: 'Carta de Controle de Deslocamento — Barragem C (MS-01 TxD, MS-02 estatístico)', cod: 'MOS-24004143-RTL-003', rev: 'Rev. 0 / Dez 2025', tipo: 'controle' },
  { doc: 'Manual de Operação, Manutenção e Vigilância — Barragens UPM', cod: 'PGS-0000-010', rev: 'rev. 23/06/2025', tipo: 'manual' },
  { doc: 'Estudo de Ruptura Hipotética (Dam Break) — Barragem B', cod: 'WA06621000-1-RH-DES-0517', rev: 'Rev. 0', tipo: 'estudo' },
  { doc: 'Fluxos de Ação do Vigilante e Matriz RACI (este documento)', cod: 'GeoGuard UPM — documento de trabalho', rev: 'em revisão', tipo: 'trabalho' },
] as const;

/**
 * Cor de borda por tipo de documento — valores EXATOS do `tipoCor` do V8:6182
 * (não a escala de severidade ANM do app; usar o vermelho de "Crítico" para uma
 * `norma` de referência confundiria o operador — achado do review #105).
 */
export const DOC_TIPO_COR: Record<DocTipo, string> = {
  norma: '#78B4CF',
  inspecao: '#A6CE3A',
  controle: '#E4A944',
  paebm: '#ef4444',
  manual: '#93A1C7',
  estudo: '#82AA8B',
  trabalho: '#94a3b8',
};

export interface MatrizComunicLinha {
  gatilho: string;
  remetente: string;
  destinatarios: string;
  prazo: string;
}

/** Matriz de comunicação — gatilho → remetente/destinatários/prazo (V8:6254-6265). */
export const MATRIZ_COMUNIC: readonly MatrizComunicLinha[] = [
  { gatilho: 'Instrumento em Atenção/Alerta/Emergência', remetente: 'GeoGuard (auto) + Vigilante', destinatarios: 'EoR, RT, JF Brasil, TetraTech', prazo: 'Imediato' },
  { gatilho: 'Perda de sinal de instrumento', remetente: 'GeoGuard (auto) + Vigilante', destinatarios: 'EoR, RT, JF Brasil, TetraTech', prazo: 'Imediato · verif. 48h' },
  { gatilho: 'Perda de sinal de câmeras', remetente: 'Vigilante / Sala', destinatarios: 'TECAL (ação) + EoR, RT, JF Brasil, TetraTech', prazo: 'Imediato' },
  { gatilho: 'Perda de sinal de tiltímetros', remetente: 'Vigilante / Sala', destinatarios: 'RT, EoR, JF Brasil + TECAL', prazo: 'Imediato' },
  { gatilho: 'Solicitação de diagnóstico técnico', remetente: 'RT Foscentro', destinatarios: 'EoR, RT, JF Brasil, TetraTech', prazo: '48h/24h/12h' },
  { gatilho: 'Baixa de TARP', remetente: 'RT Foscentro', destinatarios: 'EoR, RT, JF Brasil, TetraTech', prazo: 'Conforme nível' },
  { gatilho: 'Abertura de plano de ação', remetente: 'RT / EoR', destinatarios: 'Partes + responsável da ação', prazo: 'Ao definir' },
  { gatilho: 'Enquadramento em nível ANM', remetente: 'RT Foscentro', destinatarios: 'EoR, RT, JF Brasil, TetraTech', prazo: 'Ao enquadrar' },
  { gatilho: 'Acionamento do PAEBM / Emergência', remetente: 'RT / Coord. PAEBM', destinatarios: 'Internas + agentes externos', prazo: 'Imediato' },
  { gatilho: 'Encerramento de emergência', remetente: 'Coord. PAEBM / RT', destinatarios: 'Internas + externos notificados', prazo: 'Até 5 dias' },
] as const;
